import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import FileUpload from './components/FileUpload';
import StatsPanel from './components/StatsPanel';
import MapView from './components/MapView';
import LayerPanel from './components/LayerPanel';
import TimeSlider from './components/TimeSlider';
import Sidebar from './components/Sidebar';
import WeatherData from './pages/WeatherData';
import SiteIntelligence from './pages/SiteIntelligence';
import { supabase } from './supabaseClient';
import { DB_ACCESS_ENABLED } from './dbConfig';
import { getCached, getCachedBatch, setCache, setCacheBatch } from './utils/cache';
import {
    Users, Upload, ChevronDown, BarChart2, Menu
} from 'lucide-react';
import Fuse from 'fuse.js';

const US_REGIONS = {
    western: {
        label: 'Western',
        color: '#6366f1',
        bounds: [[32.5, -125.0], [49.0, -103.0]],
        states: ['WA', 'OR', 'CA', 'NV', 'ID', 'MT', 'WY', 'UT', 'AZ', 'CO', 'NM', 'AK', 'HI'],
    },
    central: {
        label: 'Central',
        color: '#10b981',
        bounds: [[25.8, -103.0], [49.4, -80.5]],
        boundsList: [
            [[25.8, -103.0], [49.4, -87.4]],
            [[37.7, -87.4], [49.4, -80.5]]
        ],
        states: ['ND', 'SD', 'NE', 'KS', 'MN', 'IA', 'MO', 'WI', 'IL', 'OK', 'TX', 'LA', 'AR', 'OH', 'IN', 'MI'],
    },
    eastern: {
        label: 'Eastern',
        color: '#ef4444',
        bounds: [[24.5, -87.4], [47.5, -66.9]],
        boundsList: [
            [[24.5, -87.4], [37.7, -66.9]],
            [[37.7, -80.5], [47.5, -66.9]]
        ],
        states: ['ME', 'VT', 'NH', 'MA', 'RI', 'CT', 'NY', 'NJ', 'PA', 'DE', 'MD', 'DC',
            'WV', 'VA', 'KY', 'TN', 'NC', 'SC', 'GA', 'FL', 'AL', 'MS'],
    },
};

const EUROPE_BOUNDS = [[34.0, -12.0], [72.0, 45.0]];
const EUROPE_VIEW = { center: [50, 2], zoom: 6 };
const CLIENT_PALETTE = ['#76AB3F', '#2563EB', '#D97706', '#7C3AED', '#059669', '#DB2777', '#EA580C', '#0284C7', '#9333EA', '#16A34A'];

function App() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [mapData, setMapData] = useState([]);
    const [selectedClient, setSelectedClient] = useState('All Clients');
    const [showUpload, setShowUpload] = useState(false);
    const [isEmbedded, setIsEmbedded] = useState(false);

    useEffect(() => {
        setIsEmbedded(window.self !== window.top);
    }, []);

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [expandedClients, setExpandedClients] = useState(new Set());

    const [viewMode, setViewMode] = useState('client');
    const [solarData, setSolarData] = useState(new Map());
    const [forecastData, setForecastData] = useState(new Map());
    const [spaceWeatherData, setSpaceWeatherData] = useState(null);
    const [timeOffset, setTimeOffset] = useState(0); // hours from 'now'

    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [searchQuery] = useState('');
    const [mapBounds, setMapBounds] = useState(null);

    // ── Region filter state ───────────────────────────────────────────────────
    // activeRegion: null | 'western' | 'central' | 'eastern' | 'europe'
    const [activeRegion, setActiveRegion] = useState(null);
    const [, setIsRegionDropdownOpen] = useState(false);
    const [, setIsUSSubOpen] = useState(false);
    const regionDropdownRef = useRef(null);

    // ── Toast notification state ─────────────────────────────────────────────
    const [toastMessage, setToastMessage] = useState(null);
    const toastTimerRef = useRef(null);

    // Auto-dismiss toast after animation completes (3s)
    useEffect(() => {
        if (toastMessage) {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            toastTimerRef.current = setTimeout(() => setToastMessage(null), 3100);
            return () => clearTimeout(toastTimerRef.current);
        }
    }, [toastMessage]);

    // Close region dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (regionDropdownRef.current && !regionDropdownRef.current.contains(e.target)) {
                setIsRegionDropdownOpen(false);
                setIsUSSubOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ── Fetch sites ──────────────────────────────────────────────────────────
    const fetchSites = async () => {
        if (!DB_ACCESS_ENABLED) { setMapData([]); return; }
        try {
            let allData = [], from = 0, to = 999, hasMore = true;
            while (hasMore) {
                const { data, error } = await supabase.from('sites').select('*').order('id', { ascending: true }).range(from, to);
                if (error) throw error;
                if (data?.length > 0) {
                    allData = [...allData, ...data];
                    from += 1000; to += 1000;
                    if (data.length < 1000) hasMore = false;
                } else hasMore = false;
            }
            setMapData(allData);
        } catch (e) { console.error('Failed to fetch sites:', e); }
    };

    useEffect(() => { fetchSites(); }, []);

    const handleDataLoaded = () => {
        fetchSites();
        setShowUpload(false);
        setSolarData(new Map());
    };

    useEffect(() => {
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target))
                setIsDropdownOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ── Solar fetch (with localStorage cache) ──────────────────────────────
    const fetchSolarData = useCallback(async (sites) => {
        if (!sites.length) return;
        try {
            const coords = sites.map(s => ({
                lat: parseFloat(s.Latitude || s.latitude),
                lng: parseFloat(s.Longitude || s.longitude),
                id: s.id,
            })).filter(s => !isNaN(s.lat) && !isNaN(s.lng) && !(s.lat === 0 && s.lng === 0));

            // 1. Check localStorage cache first
            const cachedResults = getCachedBatch('solar', coords);
            if (cachedResults.size > 0) {
                console.log(`[Solar] ${cachedResults.size} cache hit(s) from localStorage`);
                setSolarData(prev => {
                    const map = new Map(prev);
                    for (const [key, value] of cachedResults) {
                        map.set(key, value);
                    }
                    return map;
                });
            }

            // 2. Filter out already-cached coords
            const uncached = coords.filter(c => {
                const key = `${c.lat.toFixed(4)},${c.lng.toFixed(4)}`;
                return !cachedResults.has(key);
            });

            if (uncached.length === 0) return; // All from cache!

            // 3. Fetch uncached from API
            const res = await fetch('/api/solar', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sites: uncached }),
            });
            if (!res.ok) throw new Error(`${res.status}`);
            const { results } = await res.json();

            // 4. Store results in both state and localStorage
            setSolarData(prev => {
                const map = new Map(prev);
                for (const r of results) {
                    if (r.lat != null && r.lng != null) {
                        const key = `${r.lat.toFixed(4)},${r.lng.toFixed(4)}`;
                        map.set(key, r);
                    }
                }
                return map;
            });
            setCacheBatch('solar', results);
        } catch (err) { console.error('Solar fetch failed:', err); }
    }, []);

    // ── Forecast fetch (with localStorage cache) ─────────────────────────
    const fetchForecastData = useCallback(async (sites) => {
        if (!sites.length) return;
        try {
            const site = sites[0]; 
            const lat = parseFloat(site.Latitude || site.latitude);
            const lng = parseFloat(site.Longitude || site.longitude);
            if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return;

            const coordKey = `${lat.toFixed(4)},${lng.toFixed(4)}`;

            // 1. Check localStorage cache first
            const cached = getCached('forecast', lat, lng);
            if (cached) {
                console.log(`[Forecast] Cache HIT from localStorage for ${coordKey}`);
                setForecastData(prev => {
                    const map = new Map(prev);
                    map.set(coordKey, cached);
                    return map;
                });
                return; // No API call needed!
            }

            // 2. Fetch from API
            const res = await fetch('/api/forecast', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ lat, lng }),
            });
            if (!res.ok) throw new Error(`${res.status}`);
            const data = await res.json();

            // 3. Store in state + localStorage
            setForecastData(prev => {
                const map = new Map(prev);
                map.set(coordKey, data.forecast);
                return map;
            });
            setCache('forecast', lat, lng, data.forecast);
            console.log(`[Forecast] Cache MISS — stored in localStorage for ${coordKey}`);
        } catch (err) { 
            console.error('Forecast fetch failed:', err); 
            setForecastData(prev => {
                const map = new Map(prev);
                map.set(`${parseFloat(sites[0].lat || sites[0].Latitude || 0).toFixed(4)},${parseFloat(sites[0].lng || sites[0].Longitude || 0).toFixed(4)}`, [{ error: true }]);
                return map;
            });
        }
    }, []);

    const fetchSpaceWeather = useCallback(async () => {
        try {
            const res = await fetch('/api/space-weather');
            if (res.ok) {
                const data = await res.json();
                setSpaceWeatherData(data);
            }
        } catch (err) {
            console.error('Failed to fetch space weather:', err);
        }
    }, []);

    useEffect(() => {
        if (viewMode === 'forecast' && !spaceWeatherData) {
            fetchSpaceWeather();
        }
    }, [viewMode, spaceWeatherData, fetchSpaceWeather]);

    const handleToggleSolar = (forceSolar) => {
        const next = typeof forceSolar === 'boolean' ? (forceSolar ? 'solar' : 'client') : (viewMode === 'solar' ? 'client' : 'solar');
        if (next === viewMode) return;
        setViewMode(next);
    };



    // ── Derived data ─────────────────────────────────────────────────────────
    const clients = useMemo(() => [
        'All Clients',
        ...new Set(mapData.map(s => (s.client_name || 'General').trim().toUpperCase()).filter(Boolean)),
    ], [mapData]);

    const clientColorMap = useMemo(() => {
        const map = {};
        mapData.forEach(s => {
            const name = (s.client_name || 'General').trim().toUpperCase();
            const colorVal = s.Color || s.color;
            if (!map[name] && colorVal) {
                // Remove all spaces so things like "Dark Blue" become valid CSS "DarkBlue"
                const cTrim = colorVal.toString().trim().replace(/\s+/g, '');
                const cLower = cTrim.toLowerCase();
                if (cTrim !== '' && cLower !== 'none' && cLower !== 'n/a' && cLower !== 'null' && cLower !== 'transparent' && cLower !== 'white' && cLower !== '#ffffff') {
                    map[name] = cTrim;
                }
            }
        });
        // Fallback for clients without a valid Color field
        clients.forEach((c, i) => {
            if (c !== 'All Clients' && !map[c]) map[c] = CLIENT_PALETTE[i % CLIENT_PALETTE.length];
        });
        return map;
    }, [mapData, clients]);

    const clientRegions = useMemo(() => {
        const mapping = {};
        mapData.forEach(site => {
            const c = (site.client_name || 'General').trim().toUpperCase();
            if (!mapping[c]) mapping[c] = new Set();
            
            const lat = parseFloat(site.Latitude || site.latitude);
            const lng = parseFloat(site.Longitude || site.longitude);
            if (isNaN(lat) || isNaN(lng)) return;
            
            for (const [key, region] of Object.entries(US_REGIONS)) {
                const boxes = region.boundsList || [region.bounds];
                for (const bounds of boxes) {
                    const [[swLat, swLng], [neLat, neLng]] = bounds;
                    if (lat >= swLat && lat <= neLat && lng >= swLng && lng <= neLng) {
                        mapping[c].add(key);
                        break;
                    }
                }
            }
            const [sw, ne] = EUROPE_BOUNDS;
            if (lat >= sw[0] && lat <= ne[0] && lng >= sw[1] && lng <= ne[1]) {
                mapping[c].add('europe');
            }
        });
        return mapping;
    }, [mapData]);

    const clientFiltered = useMemo(() => selectedClient === 'All Clients'
        ? mapData
        : mapData.filter(s => (s.client_name || 'General').trim().toUpperCase() === selectedClient),
        [mapData, selectedClient]);

    const filteredData = useMemo(() => {
        if (!searchQuery.trim()) return clientFiltered;
        const q = searchQuery.trim();

        // Extract keys to search on
        const keys = ['address', 'postal_code', 'client_name', 'site', 'name', 'site_name', 'location_name'];
        const additionalKeys = mapData.length > 0 ? Object.keys(mapData[0]).filter(k => /site.*name|location.*name|^name$|^site$/i.test(k)) : [];
        const finalKeys = Array.from(new Set([...keys, ...additionalKeys]));

        const fuse = new Fuse(clientFiltered, {
            keys: finalKeys,
            threshold: 0.3, // Fuzzy match tolerance
            ignoreLocation: true,
        });

        return fuse.search(q).map(result => result.item);
    }, [clientFiltered, searchQuery, mapData]);

    const viewportFilteredData = useMemo(() => {
        if (!mapBounds) return filteredData;
        const sw = mapBounds.getSouthWest();
        const ne = mapBounds.getNorthEast();
        return filteredData.filter(site => {
            const lat = parseFloat(site.Latitude || site.latitude);
            const lng = parseFloat(site.Longitude || site.longitude);
            if (isNaN(lat) || isNaN(lng)) return false;
            return lat >= sw.lat && lat <= ne.lat && lng >= sw.lng && lng <= ne.lng;
        });
    }, [filteredData, mapBounds]);

    // ── Region-filtered data ──────────────────────────────────────────────────
    // When a US sub-region is active, only show sites whose lat/lng fall within
    // that region's bounding box. Europe shows European-coordinate sites.
    const regionFilteredData = useMemo(() => {
        if (!activeRegion) return filteredData;
        if (activeRegion === 'europe') {
            const [sw, ne] = EUROPE_BOUNDS;
            return filteredData.filter(site => {
                const lat = parseFloat(site.Latitude || site.latitude);
                const lng = parseFloat(site.Longitude || site.longitude);
                if (isNaN(lat) || isNaN(lng)) return false;
                return lat >= sw[0] && lat <= ne[0] && lng >= sw[1] && lng <= ne[1];
            });
        }
        const region = US_REGIONS[activeRegion];
        if (!region) return filteredData;
        const boxes = region.boundsList || [region.bounds];
        return filteredData.filter(site => {
            const lat = parseFloat(site.Latitude || site.latitude);
            const lng = parseFloat(site.Longitude || site.longitude);
            if (isNaN(lat) || isNaN(lng)) return false;
            return boxes.some(bounds => {
                const [[swLat, swLng], [neLat, neLng]] = bounds;
                return lat >= swLat && lat <= neLat && lng >= swLng && lng <= neLng;
            });
        });
    }, [filteredData, activeRegion]);

    // Auto-fetch solar data for the current viewport when in a solar-related mode
    useEffect(() => {
        if (viewMode !== 'client' && regionFilteredData.length > 0) {
            fetchSolarData(regionFilteredData);
        }
    }, [viewMode, regionFilteredData, fetchSolarData]);

    // eslint-disable-next-line no-unused-vars
    const handleRegionSelect = (regionKey) => {
        // If toggling off, just clear
        if (activeRegion === regionKey) {
            setActiveRegion(null);
            setIsRegionDropdownOpen(false);
            setIsUSSubOpen(false);
            return;
        }

        // Check if the new region would have any sites
        let hasSites = false;
        if (regionKey === 'europe') {
            const [sw, ne] = EUROPE_BOUNDS;
            hasSites = filteredData.some(site => {
                const lat = parseFloat(site.Latitude || site.latitude);
                const lng = parseFloat(site.Longitude || site.longitude);
                if (isNaN(lat) || isNaN(lng)) return false;
                return lat >= sw[0] && lat <= ne[0] && lng >= sw[1] && lng <= ne[1];
            });
        } else {
            const region = US_REGIONS[regionKey];
            if (region) {
                const boxes = region.boundsList || [region.bounds];
                hasSites = filteredData.some(site => {
                    const lat = parseFloat(site.Latitude || site.latitude);
                    const lng = parseFloat(site.Longitude || site.longitude);
                    if (isNaN(lat) || isNaN(lng)) return false;
                    return boxes.some(bounds => {
                        const [[swLat, swLng], [neLat, neLng]] = bounds;
                        return lat >= swLat && lat <= neLat && lng >= swLng && lng <= neLng;
                    });
                });
            }
        }

        if (!hasSites) {
            // No sites in this region — revert to View All + show toast
            setActiveRegion(null);
            setToastMessage('No sites found in this region.');
        } else {
            setActiveRegion(regionKey);
        }

        setIsRegionDropdownOpen(false);
        setIsUSSubOpen(false);
    };

    // ── Color helpers ─────────────────────────────────────────────────────────
    // Adjust the size and styling of the Header Buttons (like "All Clients" dropdown) here
    const hdrBtn = (active) => ({
        display: 'flex', alignItems: 'center', gap: 6,
        height: 36, padding: '0 12px', borderRadius: 10,
        cursor: 'pointer', fontSize: 13, fontWeight: 700,
        fontFamily: 'Inter, sans-serif', transition: 'all 0.18s',
        background: active ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)',
        border: active ? '1px solid rgba(255,255,255,0.4)' : '1px solid rgba(255,255,255,0.2)',
        color: active ? '#fff' : 'rgba(255,255,255,0.85)',
    });

    return (
        <div className="h-screen flex flex-col md:flex-row bg-[var(--bg-page)] overflow-hidden">
            {!isEmbedded && <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />}
            <div className="flex-1 flex flex-col min-w-0 min-h-0">

            {/* Render header only if NOT embedded */}
            {!isEmbedded && (
                <header className="bg-[#76AB3F] px-4 sm:px-6 flex items-center justify-between relative z-[500] shrink-0 shadow-[0_2px_16px_rgba(45,92,18,0.35),0_1px_0_rgba(255,255,255,0.1)_inset]" style={{ height: 'clamp(64px, 10vw, 80px)' }}>
                    
                    {/* Center: Title */}
                    <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', pointerEvents: 'none', width: 'max-content' }}>
                        <div style={{ fontSize: 'clamp(14px, 4vw, 26px)', fontWeight: 800, color: '#fff', letterSpacing: '0.02em', lineHeight: 1.2, textShadow: '0 2px 4px rgba(0,0,0,0.1)', whiteSpace: 'nowrap' }}>
                            AGS Geography
                        </div>
                    </div>

                    {/* Left: Mobile Sidebar Toggle */}
                    <div className="md:hidden flex items-center z-10">
                        <button
                            onClick={() => setIsSidebarOpen(true)}
                            className="p-2 -ml-2 text-white hover:bg-white/20 rounded-lg transition-colors cursor-pointer"
                        >
                            <Menu size={24} />
                        </button>
                    </div>

                    {/* Left space filler to keep Right controls on the right (since header uses justify-between) */}
                    <div className="hidden md:block"></div>

                {/* Right: Controls */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    {!showUpload && (
                        <>
                            {/* Client Dropdown */}
                            <div 
                                ref={dropdownRef} 
                                className="hidden sm:block"
                                style={{ position: 'relative' }}
                                onMouseEnter={() => { if (window.matchMedia('(hover: hover)').matches) setIsDropdownOpen(true); }}
                                onMouseLeave={() => { if (window.matchMedia('(hover: hover)').matches) setIsDropdownOpen(false); }}
                            >
                                <button
                                    onClick={() => setIsDropdownOpen(o => !o)}
                                    style={{
                                        ...hdrBtn(isDropdownOpen),
                                        maxWidth: 220, overflow: 'hidden',
                                    }}
                                >
                                    <Users size={15} style={{ flexShrink: 0 }} />
                                    <span className="inline overflow-hidden text-ellipsis whitespace-nowrap flex-1 text-left max-w-[100px] sm:max-w-none">
                                        {selectedClient === 'All Clients' ? 'All Clients' : selectedClient}
                                    </span>
                                    <ChevronDown size={15} style={{ flexShrink: 0, transform: isDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                                </button>

                                {isDropdownOpen && (
                                    <div style={{
                                        position: 'absolute', top: '100%', right: 0,
                                        width: 230, zIndex: 600,
                                        paddingTop: 8, // Bridge gap for hover
                                    }}>
                                        <div style={{
                                            background: 'rgba(255, 255, 255, 0.92)',
                                            backdropFilter: 'blur(16px)',
                                            WebkitBackdropFilter: 'blur(16px)',
                                            border: '1px solid rgba(255,255,255,0.4)',
                                            borderRadius: 14,
                                            boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                                            animation: 'zoomIn 0.15s ease-out',
                                        }}>
                                         <div style={{ padding: '6px' }}>
                                            {clients.map(client => {
                                                const hasRegions = client !== 'All Clients' && clientRegions[client] && clientRegions[client].size > 0;
                                                const isExpanded = expandedClients.has(client);
                                                
                                                return (
                                                    <div 
                                                        key={client} 
                                                        style={{ marginBottom: 2, position: 'relative' }}
                                                        onMouseEnter={() => {
                                                            if (window.matchMedia('(hover: hover)').matches) {
                                                                if (hasRegions && !isExpanded) {
                                                                    setExpandedClients(new Set([client]));
                                                                } else if (!hasRegions) {
                                                                    setExpandedClients(new Set());
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', gap: 4 }}>
                                                            <button
                                                                onClick={(e) => { 
                                                                    // On touch devices, first tap expands the regions menu if it exists
                                                                    if (!window.matchMedia('(hover: hover)').matches && hasRegions && !isExpanded) {
                                                                        e.preventDefault();
                                                                        setExpandedClients(new Set([client]));
                                                                        return;
                                                                    }
                                                                    setSelectedClient(client); 
                                                                    setActiveRegion(null); 
                                                                    setIsDropdownOpen(false); 
                                                                }}
                                                                style={{
                                                                    flex: 1, textAlign: 'left',
                                                                    padding: '8px 12px', borderRadius: 8,
                                                                    fontSize: 12, fontWeight: selectedClient === client && !activeRegion ? 700 : 500,
                                                                    color: selectedClient === client && !activeRegion ? '#4a7a28' : '#374151',
                                                                    background: selectedClient === client && !activeRegion ? 'var(--green-100)' : 'transparent',
                                                                    border: 'none', cursor: 'pointer',
                                                                    fontFamily: 'Inter, sans-serif',
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                                    transition: 'all 0.12s',
                                                                }}
                                                                onMouseEnter={e => { if (!(selectedClient === client && !activeRegion)) e.currentTarget.style.background = 'var(--green-50)'; }}
                                                                onMouseLeave={e => { if (!(selectedClient === client && !activeRegion)) e.currentTarget.style.background = 'transparent'; }}
                                                            >
                                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client}</span>
                                                                {selectedClient === client && !activeRegion && (
                                                                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#76AB3F', boxShadow: '0 0 6px rgba(118,171,63,0.5)', flexShrink: 0 }} />
                                                                )}
                                                            </button>
                                                            {client !== 'All Clients' && (
                                                                <div
                                                                    style={{
                                                                        width: 28, flexShrink: 0,
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    }}
                                                                >
                                                                    <div style={{
                                                                        width: 8, height: 8, borderRadius: '50%',
                                                                        background: clientColorMap[client] || '#6B7280',
                                                                        boxShadow: `0 0 5px ${
                                                                            (clientColorMap[client] || '#6B7280').startsWith('#') 
                                                                                ? (clientColorMap[client] || '#6B7280') + '60' 
                                                                                : (clientColorMap[client] || '#6B7280')
                                                                        }`,
                                                                        flexShrink: 0,
                                                                    }} />
                                                                </div>
                                                            )}
                                                        </div>
                                                        {hasRegions && isExpanded && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                right: '100%',
                                                                marginRight: 6,
                                                                padding: '6px',
                                                                background: 'rgba(255, 255, 255, 0.95)',
                                                                backdropFilter: 'blur(16px)',
                                                                WebkitBackdropFilter: 'blur(16px)',
                                                                border: '1px solid rgba(255,255,255,0.8)',
                                                                borderRadius: 12,
                                                                boxShadow: '-4px 4px 15px rgba(0,0,0,0.1)',
                                                                display: 'flex', 
                                                                flexDirection: 'column', 
                                                                gap: 2,
                                                                minWidth: '130px',
                                                                animation: 'zoomIn 0.15s ease-out',
                                                                zIndex: 10
                                                            }}>
                                                                <div style={{ padding: '4px 10px', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                                    {client}
                                                                </div>
                                                                {Array.from(clientRegions[client]).map(regionKey => {
                                                                    const regionLabel = regionKey === 'europe' ? 'Europe' : US_REGIONS[regionKey]?.label;
                                                                    const isActive = selectedClient === client && activeRegion === regionKey;
                                                                    return (
                                                                        <button
                                                                            key={regionKey}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setSelectedClient(client);
                                                                                setActiveRegion(regionKey);
                                                                                setIsDropdownOpen(false);
                                                                            }}
                                                                            style={{
                                                                                width: '100%', textAlign: 'left',
                                                                                padding: '6px 10px', borderRadius: 8,
                                                                                fontSize: 11, fontWeight: isActive ? 600 : 500,
                                                                                color: isActive ? '#4a7a28' : '#6b7280',
                                                                                background: isActive ? 'var(--green-50)' : 'transparent',
                                                                                border: 'none', cursor: 'pointer',
                                                                                fontFamily: 'Inter, sans-serif',
                                                                                display: 'flex', alignItems: 'center', gap: 6,
                                                                                transition: 'all 0.12s',
                                                                            }}
                                                                            onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--green-50)'; }}
                                                                            onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                                                                        >
                                                                            {regionLabel}
                                                                            {isActive && (
                                                                                <div style={{ marginLeft: 'auto', width: 4, height: 4, borderRadius: '50%', background: '#76AB3F' }} />
                                                                            )}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                         </div>
                                        </div>
                                    </div>
                                )}
                            </div>



                            {/* Removed legacy BarChart2 button */}
                        </>
                    )}


                    <button
                        onClick={() => setIsPanelOpen(p => !p)}
                        className="flex items-center gap-1.5 p-1.5 sm:px-3 sm:py-1.5 ml-1 sm:ml-2 text-[13px] font-semibold text-white rounded-md sm:rounded-lg cursor-pointer transition-all duration-200 shadow-[0_2px_10px_rgba(0,0,0,0.1)]"
                        style={{
                            background: isPanelOpen ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)',
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                            border: '1px solid rgba(255,255,255,0.3)',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)'}
                        onMouseLeave={e => e.currentTarget.style.background = isPanelOpen ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)'}
                    >
                        <BarChart2 size={16} className="w-4 h-4 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">Site Statistics</span>
                    </button>

                    <div
                        style={{
                            background: 'transparent',
                            border: 'none',
                            padding: '4px',
                            marginLeft: '4px',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <img
                            src={`${process.env.PUBLIC_URL}/ags-log-nob.png`}
                            alt="AGS"
                            className="h-9 sm:h-12 object-contain shrink-0"
                            style={{ transition: 'transform 0.2s', transformOrigin: 'right' }}
                            onMouseEnter={e => { if (!showUpload) e.currentTarget.style.transform = 'scale(1.05)' }}
                            onMouseLeave={e => { if (!showUpload) e.currentTarget.style.transform = 'scale(1)' }}
                        />
                    </div>
                </div>
            </header>
            )}

            {/* ═══════════════ MAIN AREA ═══════════════ */}
            <main style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative', width: '100%' }}>

                <StatsPanel
                    isOpen={isPanelOpen}
                    onClose={() => setIsPanelOpen(false)}
                    data={activeRegion ? regionFilteredData : mapData}
                    filteredData={activeRegion ? regionFilteredData : viewportFilteredData}
                    solarData={solarData}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    selectedClient={selectedClient}
                    showUpload={showUpload}
                    setShowUpload={setShowUpload}
                    onDataLoaded={handleDataLoaded}
                />

                {activeTab === 'weather_data' ? (
                    <WeatherData selectedClient={selectedClient} clientColorMap={clientColorMap} />
                ) : activeTab === 'site_intelligence' ? (
                    <SiteIntelligence selectedClient={selectedClient} clientColorMap={clientColorMap} />
                ) : (
                    <>

                        {showUpload ? (
                    <div style={{
                        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                        background: 'var(--bg-page)', padding: '32px 24px',
                        overflowY: 'auto',
                    }}>
                        <div style={{ maxWidth: 520, width: '100%' }}>
                            {/* Back to Map - at the top, always visible */}
                            <button
                                onClick={() => setShowUpload(false)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    marginBottom: 24, padding: '8px 16px', borderRadius: 10,
                                    cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                    fontFamily: 'Inter, sans-serif', transition: 'all 0.18s',
                                    background: '#76AB3F', border: 'none',
                                    color: '#fff', boxShadow: '0 2px 8px rgba(118,171,63,0.3)',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#5e9530'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = '#76AB3F'; }}
                            >
                                ← Back to Map
                            </button>

                            <div style={{ textAlign: 'center', marginBottom: 32 }}>
                                <div style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: 56, height: 56, borderRadius: 18,
                                    background: 'var(--green-100)',
                                    border: '1px solid var(--border-soft)',
                                    marginBottom: 16,
                                }}>
                                    <Upload size={24} style={{ color: '#76AB3F' }} />
                                </div>
                                <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-dark)', margin: '0 0 8px' }}>
                                    Visualize Your Sites
                                </h2>
                                <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
                                    Upload an Excel or CSV file to map your site locations.
                                </p>
                            </div>
                            <div style={{
                                background: '#fff',
                                border: '1px solid var(--border-soft)',
                                borderRadius: 20, padding: 24,
                                boxShadow: 'var(--shadow-md)',
                            }}>
                                <FileUpload onDataLoaded={handleDataLoaded} />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        className={`map-container ${isPanelOpen ? 'map-shifted' : ''}`}
                        style={{
                            flex: 1,
                            position: 'relative',
                            height: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        {!isPanelOpen && <LayerPanel viewMode={viewMode} setViewMode={setViewMode} isPanelOpen={isPanelOpen} />}
                        <MapView
                            data={regionFilteredData}
                            selectedClient={selectedClient}
                            viewMode={viewMode}
                            solarData={solarData}
                            forecastData={forecastData}
                            spaceWeatherData={spaceWeatherData}
                            timeOffset={timeOffset}
                            onToggleSolar={handleToggleSolar}
                            onBoundsChange={setMapBounds}
                            onSiteClick={(site) => {
                                fetchSolarData([site]);
                                fetchForecastData([site]);
                            }}
                            isPanelOpen={isPanelOpen}
                            activeRegion={activeRegion}
                            regionBounds={
                                activeRegion && activeRegion !== 'europe'
                                    ? US_REGIONS[activeRegion]?.bounds
                                    : activeRegion === 'europe' ? EUROPE_BOUNDS : null
                            }
                            regionView={
                                activeRegion && activeRegion !== 'europe' && US_REGIONS[activeRegion]?.viewCenter
                                    ? { center: US_REGIONS[activeRegion].viewCenter, zoom: US_REGIONS[activeRegion].viewZoom }
                                    : activeRegion === 'europe'
                                        ? EUROPE_VIEW
                                        : null
                            }
                            isEmbedded={isEmbedded}
                        />
                        <TimeSlider
                            timeOffset={timeOffset}
                            setTimeOffset={setTimeOffset}
                            visible={viewMode !== 'client'}
                        />
                    </div>
                )}
                    </>
                )}
            </main>
            </div>

            {/* ── Toast Notification ── */}
            {toastMessage && (
                <div className="toast-notification" key={toastMessage + Date.now()}>
                    <span className="toast-icon">🌍</span>
                    {toastMessage}
                </div>
            )}
        </div>
    );
}

export default App;
